import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Clock, ArrowUpRight } from "lucide-react";
import PageShell from "@/components/PageShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Insights = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API}/posts`)
      .then((res) => {
        if (mounted) setPosts(res.data || []);
      })
      .catch(() => mounted && setError("No pudimos cargar los insights."))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <PageShell testid="insights-page">
      <section className="max-w-5xl mx-auto px-6 lg:px-10 pt-16 pb-12">
        <div className="flex items-center gap-3 mb-6">
          <span className="h-px w-10 bg-[#E6D5B8]" />
          <span className="text-[11px] tracking-luxury uppercase text-[#E6D5B8] font-medium">
            EMB Insights
          </span>
        </div>
        <h1
          data-testid="insights-headline"
          className="font-display text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05] max-w-3xl"
        >
          Análisis y{" "}
          <span className="italic text-[#E6D5B8]">criterio editorial</span>{" "}
          para construir patrimonio.
        </h1>
        <p className="mt-6 text-base lg:text-lg text-[#F9F6F0]/65 font-light max-w-2xl">
          Notas cortas y rigurosas sobre retiro, inversión, protección
          patrimonial y los principios que aplicamos en cada plan.
        </p>
      </section>

      <section className="max-w-5xl mx-auto px-6 lg:px-10 pb-20">
        {loading && (
          <div className="grid md:grid-cols-2 gap-6">
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-64 bg-[#150A1F] border border-[#2A0A4A] rounded-sm animate-pulse"
              />
            ))}
          </div>
        )}

        {!loading && error && (
          <div
            data-testid="insights-error"
            className="text-sm text-[#F9F6F0]/60 border border-white/10 rounded-sm p-8"
          >
            {error}
          </div>
        )}

        {!loading && !error && posts.length === 0 && (
          <div
            data-testid="insights-empty"
            className="text-sm text-[#F9F6F0]/60 border border-white/10 rounded-sm p-8"
          >
            Pronto publicaremos nuestros primeros análisis. Vuelve en unos
            días.
          </div>
        )}

        {!loading && !error && posts.length > 0 && (
          <div className="grid md:grid-cols-2 gap-6 lg:gap-7">
            {posts.map((p, idx) => (
              <Link
                key={p.slug}
                to={`/insights/${p.slug}`}
                data-testid={`insight-card-${p.slug}`}
                className="group relative bg-[#150A1F] border border-[#2A0A4A] rounded-sm p-8 lg:p-10 hover:border-[#E6D5B8]/45 hover:-translate-y-0.5 transition-all duration-500 flex flex-col"
              >
                <div className="flex items-center justify-between mb-6">
                  <span className="text-[10px] tracking-luxury uppercase text-[#E6D5B8] bg-[#3D1565]/30 border border-[#3D1565] px-3 py-1.5 rounded-sm">
                    {p.category}
                  </span>
                  <span className="font-display text-2xl text-[#F9F6F0]/20 italic">
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                </div>

                <h2 className="font-display text-2xl lg:text-3xl font-light leading-tight text-[#F9F6F0] group-hover:text-[#E6D5B8] transition-colors">
                  {p.title}
                </h2>

                <p className="mt-4 text-sm text-[#F9F6F0]/65 leading-relaxed font-light flex-1">
                  {p.excerpt}
                </p>

                <div className="mt-7 pt-5 border-t border-white/10 flex items-center justify-between text-xs text-[#F9F6F0]/55">
                  <div className="flex items-center gap-4">
                    <span>{p.author}</span>
                    <span className="flex items-center gap-1.5">
                      <Clock size={11} />
                      {p.reading_minutes} min
                    </span>
                  </div>
                  <span className="text-[10px] tracking-luxury uppercase">
                    {p.published_at}
                  </span>
                </div>

                <ArrowUpRight
                  size={18}
                  className="absolute top-7 right-7 text-[#E6D5B8] opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                />
              </Link>
            ))}
          </div>
        )}
      </section>
    </PageShell>
  );
};

export default Insights;
